/** @jsxImportSource @emotion/react */
import { useState, useEffect, useMemo, useRef } from 'react';
import { addClass, hasClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import { Button } from '@mui/material';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import { Code } from 'models/common/CommonCode';
import { useCommonModal } from 'hooks/useCommonModal';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CrudCode } from 'models/common/Edit';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { getExcelFileName } from 'utils/ExcelUtil';
import { hasRole } from 'utils/SessionUtil';
import {
  findMdIvesAtcRegList,
  findUsedMdIvesAticle,
  saveMdIvesAtcRegList,
} from 'apis/md/MdIvesAtcRegt';
import { MdIvesAtcReg, MdIvesAtcRegCondition } from 'models/md/MdIvesActReg';

/**
 * @desc 투자비항목 등록
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.05.14   jihwan89       페이지 생성 및 기능 추가
 */

const MdIvesAtcRegPage = () => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const { isLoading, openLoading } = useLoading();

  const initCondition: MdIvesAtcRegCondition = {
    stndYn: '',
    ivesAtcNm: '',
  };

  const [stndYnCode, setStndYnCode] = useState<Code[]>([]);
  const [condition, setCondition] = useState<MdIvesAtcRegCondition>(initCondition);
  const [rowData, setRowData] = useState<MdIvesAtcReg[]>([]);
  const [flexRef, setFlexRef] = useState<any>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const initCodes = async () => {
    const stndYnCd = await getCommonCodeNames('STND_YN');

    setStndYnCode(stndYnCd);
  };

  useEffect(() => {
    initCodes();
    btnSearch();
    // ADMIN, 형개발 기준정보 관리자, 형개발 투자 관리자
    if (hasRole('MD_IVES_MANAGER') || hasRole('ADM')) {
      setHasAuth(true);
    }
  }, []);

  const handleOnChange = (name: string, value: any) => {
    setCondition((prev) => ({ ...prev, [name]: value }));
  };

  const btnSearch = () => {
    findMdIvesAtcRegList(condition).then((result: any) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const layoutDefinition = useMemo(() => {
    const stndYnCodeMap = new DataMap(stndYnCode, 'cmnCd', 'cmnCdNm');
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      // {
      //   binding: 'no',
      //   header: String(t('com.label.NO', 'NO')),
      //   minWidth: 40,
      //   width: 40,
      //   isReadOnly: true,
      //   align: 'center',
      //   cellTemplate: (grid) => grid.row._idx + 1,
      // },
      {
        binding: 'stndYn',
        header: String(t('md.label.표준여부', '표준여부')),
        align: 'center',
        minWidth: 40,
        width: 250,
        dataMap: stndYnCodeMap,
        isReadOnly: !hasAuth,
      },
      {
        binding: 'ivesAtcNm',
        header: String(t('md.label.투자항목', '투자항목')),
        align: 'center',
        minWidth: 40,
        width: '*',
        isReadOnly: !hasAuth,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        align: 'center',
        minWidth: 40,
        width: 200,
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        align: 'center',
        minWidth: 40,
        width: 200,
        isReadOnly: true,
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      const item = e.panel.rows[e.row]?.dataItem;
      if (e.panel.cellType === CellType.TopLeft && e.cell.querySelector('input').indeterminate) {
        e.cell.querySelector('input').checked = true;
      }
      if (e.panel === s.rowHeaders) {
        if (e.cell.hasChildNodes()) {
          // 이미 사용중인 항목인 경우 선택불가/readonly
          if (item?.usedYn === 'Y') {
            addClass(e.cell, 'wj-state-disabled');
          }
        }
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      // 전체 선택 시
      if (hasClass(e.target, 'wj-column-selector-group') && e.target.checked) {
        grid.deferUpdate(() => {
          grid.rows.forEach((row) => {
            if (row?.dataItem?.usedYn === 'Y') {
              row.isSelected = false;
            }
          });
        });
      }
    });
  };

  const btnSave = async () => {
    const saveRowData = rowData.filter(
      (element) =>
        element.crudKey == CrudCode.CREATE ||
        element.crudKey == CrudCode.UPDATE ||
        element.crudKey == CrudCode.DELETE
    );

    const delRowData = rowData.filter((element) => element.crudKey == CrudCode.DELETE);
    if (delRowData.length > 0) {
      const used = await findUsedMdIvesAticle(delRowData);
      if (used.data !== null && used.data !== '') {
        openMessageBar({
          type: 'error',
          content:
            used.data +
            ': ' +
            t(
              'md.label.사용중인 기준정보 입니다. 삭제할 수 없습니다.',
              '사용중인 기준정보 입니다. 삭제할 수 없습니다.'
            ),
        });
        return false;
      }
    }

    const valid = saveRowData
      .map((rowNode, index) => {
        if (rowNode.crudKey != CrudCode.DELETE) {
          if (rowNode.stndYn == null || rowNode.stndYn == '')
            return `${t('md.label.표준여부를 선택해 주세요.', '표준여부를 선택해 주세요.')}`;
          if (rowNode.ivesAtcNm == null || rowNode.ivesAtcNm == '')
            return `${t('md.label.투자항목을 입력해 주세요.', '투자항목을 입력해 주세요.')}`;
        }
      })
      .filter((element) => element !== undefined);
    if (valid.length) {
      const content = valid[0]?.toString();
      openMessageBar({ type: 'error', content: content || '' });
      return false;
    }

    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`),
      yesCallback: () => {
        saveMdIvesAtcRegList(saveRowData)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.저장 되었습니다.', '저장 되었습니다.'),
              });
              btnSearch();
            } else {
              openMessageBar({
                type: 'confirm',
                content: t('com.msg.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.msg.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  const btnAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
    } as MdIvesAtcReg;
    setRowData([newRow, ...rowData]);
  };

  const btnDelRow = () => {
    if (flexRef === null) {
      openMessageBar({
        type: 'error',
        content: t('md.label.기준정보를 선택해 주세요.', '기준정보를 선택해 주세요.'),
      });
      return false;
    }

    const selectedRowNodes = flexRef.selectedRows;
    const isSelected = selectedRowNodes.filter((item) => item.isSelected);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('md.label.기준정보를 선택해 주세요.', '기준정보를 선택해 주세요.'),
      });
      return false;
    } else {
      const selectedIds = selectedRowNodes
        .map((rowNode) => {
          return parseInt(rowNode._idx!);
        })
        .reverse();

      selectedIds.forEach((idx) => {
        if (rowData[idx].crudKey === CrudCode.CREATE) {
          delete rowData[idx];
        } else {
          rowData[idx].crudKey = CrudCode.DELETE;
        }
      });

      const filteredData = rowData.filter((element) => element !== undefined);
      setRowData(filteredData);
    }
  };

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = '투자항목 기준정보';
    book.saveAsync(getExcelFileName(t('md.label.투자항목 기준정보', '투자항목 기준정보')));
  };

  const HandleOnKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('md.label.표준여부', '표준여부'))}</label>
                <ComboBox
                  options={stndYnCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={condition?.stndYn}
                  onChange={(value) => {
                    handleOnChange('stndYn', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('md.label.투자항목', '투자항목'))}</label>
                <CustomInputWithSearch
                  type="text"
                  name="ivesAtcNm"
                  value={condition?.ivesAtcNm}
                  defaultValue={condition?.ivesAtcNm}
                  placeholder={String(
                    t('com.label.투자항목을 입력해 주세요.', '투자항목을 입력해 주세요.')
                  )}
                  onChange={(e) => {
                    handleOnChange('ivesAtcNm', e.target.value);
                  }}
                  onKeyDown={HandleOnKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('md.label.투자항목', '투자항목')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {hasAuth && (
            <Button css={IconButton.button} className="addRow" onClick={btnAddRow} disableRipple>
              {t('com.button.행추가', '행추가')}
            </Button>
          )}
          {hasAuth && (
            <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
              {t('com.button.행삭제', '행삭제')}
            </Button>
          )}
          {/* {hasAuth && (
            <Button css={IconButton.button} className="copyRow" onClick={btnCopyRow} disableRipple>
              {t('com.button.행복사', '행복사')}
            </Button>
          )} */}
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch} disableRipple>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isReadOnly={false}
        allowPinning={false}
        initialized={onInitialized}
        height={589}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default MdIvesAtcRegPage;
